/**
 * @fileoverview Shared state and refs for the Chat page hooks.
 * @module pages/chat/useChatContext
 */
import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import useRequireAccess from "../../hooks/useRequireAccess.js";
import useDeviceDetection from "../../hooks/useDeviceDetection.js";
import { canAccessChat } from "../../utils/accessControl.js";

/** Holds Chat page state, router helpers, and refs. */
const useChatContext = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { allowed } = useRequireAccess(canAccessChat);
  const { isTablet, isSmallMobile, isMediumMobile, isMobile } =
    useDeviceDetection();

  const ws = useRef(null);
  const messageEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const isUserActiveRef = useRef(true);
  const lastSeenMessageTimestampByChatRef = useRef({});
  const pendingFileUploads = useRef(new Map());
  const selectedChatRef = useRef(null);

  const [messageInput, setMessageInput] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companies, setCompanies] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeConnections, setActiveConnections] = useState([]);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isUserActive, setIsUserActive] = useState(true);
  const [activeView, setActiveView] = useState("inbox");
  const [connectionRequests, setConnectionRequests] = useState([]);
  const [connectionRequestsLoading, setConnectionRequestsLoading] =
    useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState("");
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [isReadOnlyChat, setIsReadOnlyChat] = useState(false);
  const [readOnlyHeaderTitle, setReadOnlyHeaderTitle] = useState(null);
  const [readOnlyRequestType, setReadOnlyRequestType] = useState(null);
  const [readOnlyRequest, setReadOnlyRequest] = useState(null);
  const [showChatInTablet, setShowChatInTablet] = useState(false);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  const setChatParticipants = useCallback(
    ({ users: participantUsers, companies: participantCompanies }) => {
      setUsers(participantUsers);
      setCompanies(participantCompanies);
    },
    [],
  );

  const searchBarDevice = useMemo(() => {
    if (isSmallMobile) {
      return "smallMobile";
    } else if (isMediumMobile) {
      return "mediumMobile";
    } else if (isTablet) {
      return "tablet";
    } else {
      return "desktop";
    }
  }, [isSmallMobile, isMediumMobile, isTablet]);

  const isCompactViewport = isMobile || isTablet;

  return {
    t,
    navigate,
    location,
    allowed,
    isCompactViewport,
    searchBarDevice,
    ws,
    messageEndRef,
    reconnectTimeoutRef,
    reconnectAttemptsRef,
    lastActivityRef,
    isUserActiveRef,
    lastSeenMessageTimestampByChatRef,
    pendingFileUploads,
    selectedChatRef,
    messageInput,
    setMessageInput,
    users,
    setUsers,
    selectedUser,
    setSelectedUser,
    selectedCompany,
    setSelectedCompany,
    companies,
    setCompanies,
    setChatParticipants,
    searchQuery,
    setSearchQuery,
    chats,
    setChats,
    selectedChat,
    setSelectedChat,
    chatMessages,
    setChatMessages,
    loading,
    setLoading,
    activeConnections,
    setActiveConnections,
    isReconnecting,
    setIsReconnecting,
    isUserActive,
    setIsUserActive,
    activeView,
    setActiveView,
    connectionRequests,
    setConnectionRequests,
    connectionRequestsLoading,
    setConnectionRequestsLoading,
    showFileUpload,
    setShowFileUpload,
    selectedFile,
    setSelectedFile,
    isDragging,
    setIsDragging,
    fileError,
    setFileError,
    isFileUploading,
    setIsFileUploading,
    isReadOnlyChat,
    setIsReadOnlyChat,
    readOnlyHeaderTitle,
    setReadOnlyHeaderTitle,
    readOnlyRequestType,
    setReadOnlyRequestType,
    readOnlyRequest,
    setReadOnlyRequest,
    showChatInTablet,
    setShowChatInTablet,
  };
};

export default useChatContext;
